/**
 * Search text normalization.
 *
 * The recipe catalog mixes French sources (Ricardo, Trois fois par jour) with
 * English ones (NYT, Bon Appétit), so the same dish shows up as both "Bol poké"
 * and "Poke Bowl". Matching on raw text means those two never meet — searching
 * `poké` used to hide every unaccented spelling and vice versa.
 *
 * Everything that compares a query against recipe text must fold both sides
 * through `foldText` first.
 */

/**
 * Characters NFD decomposition leaves alone. `œ` and `ø` are distinct letters
 * rather than a base letter plus a combining mark, so stripping diacritics does
 * nothing to them — they need an explicit map.
 */
const LIGATURES: Record<string, string> = {
  œ: 'oe',
  æ: 'ae',
  ß: 'ss',
  ø: 'o',
  đ: 'd',
  ł: 'l',
  ŧ: 't',
};

const COMBINING_MARKS = /[̀-ͯ]/g;
const LIGATURE_CHARS = /[œæßøđłŧ]/g;
const APOSTROPHES = /[‘’ʼ`´]/g;
const DASHES = /[‐-―−]/g;

/**
 * Fold text to a comparable form: no accents, no ligatures, lowercase, single
 * spaces, normalized quotes and dashes.
 *
 * Handles NFC and NFD input alike — scraped French titles arrive in both forms,
 * and a precomposed `é` (U+00E9) compares unequal to a decomposed `e` + U+0301
 * even though they render identically.
 *
 * @example
 * foldText('Bol poké')      // 'bol poke'
 * foldText('Crème Brûlée')  // 'creme brulee'
 * foldText('Bœuf')          // 'boeuf'
 */
export function foldText(input: string | null | undefined): string {
  if (!input) return '';
  return input
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .toLowerCase()
    .replace(LIGATURE_CHARS, (c) => LIGATURES[c] ?? c)
    .replace(APOSTROPHES, "'")
    .replace(DASHES, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Split folded text into comparable word tokens.
 *
 * Token boundaries are what stop `ail` (garlic) from matching *cocktail*,
 * *travail* and *ailes* — the old substring matcher had no notion of a word.
 *
 * Single-character fragments are dropped, which also disposes of the French
 * elision leftovers (`d'agneau` → `agneau`).
 */
export function tokenize(input: string | null | undefined): string[] {
  const folded = foldText(input);
  if (!folded) return [];
  return folded.split(/[^\p{L}\p{N}]+/u).filter((t) => t.length > 1);
}

/**
 * Reduce a token to a crude stem so singular and plural forms meet.
 *
 * Deliberately conservative: it only handles the high-frequency regular cases
 * (FR `-s`/`-x`, EN `-s`/`-ies`/`-oes`/`-ed`/`-ing`). Anything subtler is left
 * to the fuzzy tier in the scorer, which absorbs a one-character difference
 * anyway. An aggressive stemmer creates false matches that are much harder to
 * debug than a missed one.
 *
 * Applied symmetrically to the query and the index, so imperfect stems still
 * match each other.
 *
 * @example
 * stemToken('pates')    // 'pate'  (matches 'pâtes' → 'pates' → 'pate')
 * stemToken('gateaux')  // 'gateau'
 * stemToken('tomatoes') // 'tomato'
 */
export function stemToken(token: string): string {
  if (token.length <= 3) return token;
  const stemmed = token
    .replace(/ies$/, 'y')
    .replace(/ie$/, 'y')
    .replace(/oes$/, 'o')
    .replace(/(?:ed|ing)$/, '')
    .replace(/[sx]$/, '');
  // Never stem a word out of existence.
  return stemmed.length >= 2 ? stemmed : token;
}

/** Convenience: tokenize then stem, preserving order. */
export function tokenizeStems(input: string | null | undefined): string[] {
  return tokenize(input).map(stemToken);
}
